import type { ReactNode } from 'react';
import { Sentry } from '@/lib/sentry';

interface FallbackProps {
  error: Error;
  eventId: string | null;
  resetError: () => void;
}

function ErrorFallback({ eventId, resetError }: FallbackProps) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <div className="flex flex-col items-center gap-6 text-center max-w-md">
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center overflow-hidden shadow-lg shadow-orange-500/20">
          <img src="/blazepos-logo.png" alt="BlazePOS" className="w-16 h-16 object-contain" />
        </div>

        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-bold text-white">Something went wrong</h1>
          <p className="text-gray-400 text-sm">
            An unexpected error occurred in the admin console. Our team has been
            automatically notified.
          </p>
        </div>

        {eventId && (
          <div className="w-full p-3 rounded-lg bg-gray-800/60 border border-gray-700 text-left">
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1">
              Error ID (for support)
            </p>
            <code className="text-xs font-mono text-gray-200 break-all">{eventId}</code>
          </div>
        )}

        <div className="flex flex-col gap-3 w-full">
          <button
            onClick={() => window.location.reload()}
            className="w-full px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white text-sm font-semibold hover:from-orange-600 hover:to-orange-700 transition-all"
          >
            Refresh Page
          </button>
          <button
            onClick={resetError}
            className="w-full px-4 py-2 rounded-xl border border-gray-700 text-gray-300 text-sm font-medium hover:bg-gray-800 transition-colors"
          >
            Try to Continue
          </button>
        </div>
      </div>
    </div>
  );
}

export function AppErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, eventId, resetError }) => (
        <ErrorFallback error={error as Error} eventId={eventId} resetError={resetError} />
      )}
      showDialog={false}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
